import React, { useRef, useState, useCallback } from 'react';
import { parseOHLCVCsv, parseOptionsCsv } from '../../data/parsers';
import { useStore } from '../../store';

interface CSVImportProps {
  type: 'ohlcv' | 'options';
  symbol: string;
  hasData: boolean;
}

export function CSVImport({ type, symbol, hasData }: CSVImportProps) {
  const { setCandles, setOptions } = useStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'warn'; msg: string } | null>(null);

  const processFile = useCallback(async (file: File) => {
    if (!file) return;
    setStatus(null);
    const text = await file.text();

    if (type === 'ohlcv') {
      const result = parseOHLCVCsv(text);
      if (!result.data.length) {
        setStatus({ type: 'error', msg: result.errors[0] || 'No valid rows parsed' });
        return;
      }
      setCandles(symbol, result.data, 'csv');
      const warnStr = result.warnings.length ? ` (${result.warnings.length} warnings)` : '';
      setStatus({ type: result.warnings.length ? 'warn' : 'success', msg: `Loaded ${result.data.length} candles from "${file.name}"${warnStr}` });
    } else {
      const result = parseOptionsCsv(text);
      if (!result.data.length) {
        setStatus({ type: 'error', msg: result.errors[0] || 'No valid rows parsed' });
        return;
      }
      setOptions(symbol, result.data);
      setStatus({ type: 'success', msg: `Loaded ${result.data.length} strikes from "${file.name}"` });
    }
  }, [type, symbol, setCandles, setOptions]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const hint = type === 'ohlcv'
    ? 'Columns: timestamp, open, high, low, close, volume'
    : 'Columns: strike, call_oi, put_oi (gamma_estimate optional)';

  const statusColor = status?.type === 'error' ? 'var(--red)' : status?.type === 'warn' ? 'var(--amber)' : 'var(--green)';

  return (
    <div>
      <div
        className={`drop-zone ${dragging ? 'drag' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" accept=".csv,.txt" onChange={e => { if (e.target.files?.[0]) processFile(e.target.files[0]); e.target.value = ''; }} />
        <div style={{ fontSize: 11, color: 'var(--t2)' }}>
          {hasData ? '✓ Data loaded — click or drop to replace' : 'Drop CSV or click to browse'}
        </div>
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 3 }}>{hint}</div>
      </div>
      {status && (
        <div style={{ fontSize: 10, color: statusColor, marginTop: 4, lineHeight: 1.5 }}>{status.msg}</div>
      )}
    </div>
  );
}
